/**
 * LeagueSeasonsPage — /league/:leagueId/seasons
 * ─────────────────────────────────────────────────────────────────
 * Phase 2c. Manage seasons + the teams participating in each.
 *
 * Top-level layout: a season picker + a Start-New-Season button.
 * Below the picker, the selected season's teams list + invite UI.
 *
 * Three flows:
 *   1. Start a new season (with optional carry-forward from a prior
 *      season — teams, divisions, both, neither).
 *   2. Invite a coach (by email) to attach their team to the
 *      currently-selected season. Pre-pick division + jersey
 *      colors as suggestions.
 *   3. View / cancel / resend pending invites for the selected
 *      season.
 *
 * Permissions (mirror backend gates exactly):
 *   • Any member can view seasons + teams.
 *   • Creating a season:        manage_league_settings.
 *   • Inviting a team:          manage_teams.
 *   • Cancel/resend invite:     manage_teams.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Calendar, Plus, X, Save, Trash2, Users, Mail, ChevronDown,
  CheckCircle2, Copy, RotateCw, AlertCircle, ArrowRight, ArrowLeft,
  MessageSquare,
} from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import LeagueSubNav from './LeagueSubNav.jsx';
import { useLeagueMembership } from '../../hooks/useLeagueMembership.js';
// v2.5.124 — centralized date formatter.
import { formatDateRange } from '../../utils/formatDate.js';

export default function LeagueSeasonsPage() {
  const { leagueId } = useParams();
  const { isManager, can, loading: roleLoading } = useLeagueMembership(leagueId);
  const canCreate = isManager || can('manage_league_settings');
  const canInvite = isManager || can('manage_teams');

  const [league, setLeague]   = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);

  // Default season: 'active' first, else most recent.
  const defaultSeasonId = useMemo(() => {
    if (seasons.length === 0) return '';
    const active = seasons.find(s => s.status === 'active');
    if (active) return active.id;
    return seasons[0].id;  // backend returns DESC by start_date
  }, [seasons]);

  async function loadAll() {
    if (!leagueId) return;
    try {
      const [leagueRes, seasonsRes] = await Promise.all([
        api.get(`/leagues/${leagueId}`),
        api.get(`/leagues/${leagueId}/seasons`),
      ]);
      setLeague(leagueRes?.data?.league || null);
      setSeasons(seasonsRes?.data?.seasons || []);
    } catch (err) {
      if (err?.response?.status === 403) toast.error('You are not a member of this league');
      else if (err?.response?.status !== 401) toast.error('Could not load seasons');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [leagueId]);

  useEffect(() => {
    if (!selectedSeasonId && defaultSeasonId) setSelectedSeasonId(defaultSeasonId);
  }, [defaultSeasonId, selectedSeasonId]);

  if (loading || roleLoading) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 980 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>
      </div>
    );
  }
  if (!league) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 980 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>
          League not found or no access.
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" style={{ maxWidth: 980 }}>
      <LeagueSubNav league={league} />

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 className="section-title text-xl">Seasons</h2>
          <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>
            Each season has its own schedule, teams, and standings. Carry forward teams and divisions
            from a prior season when starting a new one.
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            className="btn-ice"
            onClick={() => setShowWizard(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <Plus size={14} />
            Start new season
          </button>
        )}
      </div>

      {seasons.length === 0 ? (
        <EmptySeasonsCard canCreate={canCreate} onCreate={() => setShowWizard(true)} />
      ) : (
        <>
          <SeasonPicker
            seasons={seasons}
            value={selectedSeasonId}
            onChange={setSelectedSeasonId}
          />
          {selectedSeasonId && (
            <SeasonDetail
              leagueId={leagueId}
              seasonId={selectedSeasonId}
              canInvite={canInvite}
            />
          )}
        </>
      )}

      {showWizard && (
        <StartSeasonWizard
          leagueId={leagueId}
          existingSeasons={seasons}
          onClose={() => setShowWizard(false)}
          onCreated={async (created) => {
            setShowWizard(false);
            await loadAll();
            if (created?.id) setSelectedSeasonId(created.id);
          }}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Empty state
// ───────────────────────────────────────────────────────────────

function EmptySeasonsCard({ canCreate, onCreate }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
      <Calendar size={40} color="var(--text-2)" style={{ margin: '0 auto 14px', opacity: 0.5 }} />
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>No seasons yet</div>
      <div style={{ fontSize: 13, color: 'var(--text-2)', maxWidth: 420, margin: '0 auto 18px' }}>
        Create your first season to start inviting teams and scheduling games.
      </div>
      {canCreate ? (
        <button type="button" className="btn-ice" onClick={onCreate}>
          <Plus size={14} style={{ marginRight: 8 }} />
          Start new season
        </button>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
          A manager or someone with <code>manage_league_settings</code> can create the first season.
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Season picker
// ───────────────────────────────────────────────────────────────

function SeasonPicker({ seasons, value, onChange }) {
  return (
    <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <Calendar size={16} color="var(--ice)" />
      <span style={{ fontSize: 13, fontWeight: 600 }}>Season:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, width: 'auto', minWidth: 240, flex: '1 1 280px' }}
      >
        {seasons.map(s => (
          <option key={s.id} value={s.id}>
            {s.name} · {formatDateRange(s.start_date, s.end_date)} · {s.status}
          </option>
        ))}
      </select>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Season detail — teams list + invites
// ───────────────────────────────────────────────────────────────

function SeasonDetail({ leagueId, seasonId, canInvite }) {
  const [teams,   setTeams]   = useState([]);
  const [invites, setInvites] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  // v2.5.171 — the set of team IDs the VIEWER is actually a member of.
  // The team chat is team-scoped and the backend requires a
  // team_members row (a pure league admin who isn't on the team gets a
  // 403). So we only surface a "Chat" shortcut for league teams the
  // viewer belongs to — we don't widen chat access to league staff who
  // aren't on the team (keeps chat strictly coaches + teammates).
  const [myTeamIds, setMyTeamIds] = useState(() => new Set());

  async function loadAll() {
    setLoading(true);
    try {
      const [tRes, iRes, dRes, myRes] = await Promise.all([
        api.get(`/leagues/${leagueId}/seasons/${seasonId}/teams`).catch(() => ({ data: { teams: [] } })),
        api.get(`/leagues/${leagueId}/seasons/${seasonId}/team-invites`).catch(() => ({ data: { invites: [] } })),
        api.get(`/leagues/${leagueId}/seasons/${seasonId}/divisions`).catch(() => ({ data: { divisions: [] } })),
        api.get('/teams').catch(() => ({ data: [] })),
      ]);
      setTeams(tRes.data?.teams || []);
      setInvites(iRes.data?.invites || []);
      setDivisions(dRes.data?.divisions || []);
      setMyTeamIds(new Set((myRes.data || []).map(t => t.id)));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [seasonId]);

  if (loading) return <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>;

  const pendingInvites = invites.filter(i => !i.accepted_at && !i.cancelled_at);
  const otherInvites   = invites.filter(i =>  i.accepted_at ||  i.cancelled_at);

  return (
    <>
      {/* Teams in this season */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
          {teams.length} team{teams.length === 1 ? '' : 's'} in this season
        </span>
        {canInvite && (
          <button type="button" className="btn-ice" onClick={() => setShowInvite(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Mail size={13} />
            Invite team
          </button>
        )}
      </div>

      {teams.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 32, color: 'var(--text-2)', fontSize: 13, marginBottom: 16 }}>
          <Users size={32} style={{ margin: '0 auto 10px', opacity: 0.5 }} />
          No teams in this season yet. Invite a coach by email to attach their team.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
          {teams.map((t, i) => (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '12px 16px',
              borderBottom: i === teams.length - 1 ? 'none' : '1px solid var(--border)',
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'var(--ice-a10)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Users size={14} color="var(--ice)" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{t.team_name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>
                  {t.division_name ? `${t.division_name}` : <span style={{ fontStyle: 'italic', opacity: 0.7 }}>No division</span>}
                  {t.jersey_color_primary && (
                    <>
                      {' · '}
                      <span style={{
                        display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                        background: t.jersey_color_primary,
                        verticalAlign: 'middle', marginRight: 4,
                      }} />
                      {t.jersey_color_primary}
                    </>
                  )}
                </div>
              </div>
              {t.status === 'withdrawn' && (
                <span className="badge" style={{
                  background: 'var(--surface-2)', color: 'var(--text-2)',
                  fontSize: 10, padding: '2px 8px',
                }}>
                  Withdrawn
                </span>
              )}
              {/* v2.5.171 — Chat shortcut. Only for teams the viewer is a
                  member of (chat is team-scoped; non-members get a 403).
                  Opens the same team chat standalone teams use. */}
              {t.status !== 'withdrawn' && myTeamIds.has(t.team_id) && (
                <Link
                  to={`/team/${t.team_id}/chat`}
                  title="Open this team's chat"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '5px 10px', borderRadius: 8,
                    background: 'var(--ice-a10)', color: 'var(--ice)',
                    fontSize: 12, fontWeight: 600, textDecoration: 'none',
                    flexShrink: 0,
                  }}>
                  <MessageSquare size={13} />
                  Chat
                </Link>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pending invites */}
      {pendingInvites.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
            Pending invites ({pendingInvites.length})
          </div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {pendingInvites.map((inv, i) => (
              <InviteRow
                key={inv.id}
                invite={inv}
                isLast={i === pendingInvites.length - 1}
                leagueId={leagueId}
                seasonId={seasonId}
                canManage={canInvite}
                onChange={loadAll}
              />
            ))}
          </div>
        </div>
      )}

      {/* Accepted / cancelled invites (collapsed by default) */}
      {otherInvites.length > 0 && (
        <details style={{ marginBottom: 16 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-2)', padding: '6px 0' }}>
            History ({otherInvites.length})
          </summary>
          <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 8 }}>
            {otherInvites.map((inv, i) => (
              <InviteRow
                key={inv.id}
                invite={inv}
                isLast={i === otherInvites.length - 1}
                leagueId={leagueId}
                seasonId={seasonId}
                canManage={false}  // history is read-only
                onChange={loadAll}
              />
            ))}
          </div>
        </details>
      )}

      {showInvite && (
        <InviteTeamModal
          leagueId={leagueId}
          seasonId={seasonId}
          divisions={divisions}
          onClose={() => setShowInvite(false)}
          onSent={async () => {
            setShowInvite(false);
            await loadAll();
          }}
        />
      )}
    </>
  );
}

function InviteRow({ invite, isLast, leagueId, seasonId, canManage, onChange }) {
  const pending = !invite.accepted_at && !invite.cancelled_at;

  async function resend() {
    try {
      await api.post(`/leagues/${leagueId}/seasons/${seasonId}/team-invites/${invite.id}/resend`);
      toast.success('Invite resent');
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not resend');
    }
  }
  async function cancel() {
    const ok = window.confirm(`Cancel the invite to ${invite.email}?`);
    if (!ok) return;
    try {
      await api.post(`/leagues/${leagueId}/seasons/${seasonId}/team-invites/${invite.id}/cancel`);
      toast.success('Invite cancelled');
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not cancel');
    }
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 14px',
      borderBottom: isLast ? 'none' : '1px solid var(--border)',
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: 8,
        background: pending ? 'var(--ice-a10)' : 'var(--surface-2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {invite.cancelled_at ? <X size={13} color="var(--text-2)" />
          : invite.accepted_at ? <CheckCircle2 size={13} color="var(--goal)" />
          : <Mail size={13} color="var(--ice)" />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {invite.email}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>
          {invite.accepted_at ? `Accepted as ${invite.accepted_team_name || 'a team'}`
            : invite.cancelled_at ? 'Cancelled'
            : invite.division_name ? `Suggested division: ${invite.division_name}` : 'Pending'}
        </div>
      </div>
      {canManage && pending && (
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" className="btn-ghost" style={tinyBtn} onClick={resend}>
            <RotateCw size={11} style={{ marginRight: 4 }} />
            Resend
          </button>
          <button type="button" className="btn-ghost" style={{ ...tinyBtn, color: 'var(--penalty)' }} onClick={cancel}>
            <Trash2 size={11} />
          </button>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Invite team modal
// ───────────────────────────────────────────────────────────────

function InviteTeamModal({ leagueId, seasonId, divisions, onClose, onSent }) {
  const [email, setEmail] = useState('');
  const [divisionId, setDivisionId] = useState('');
  const [primary, setPrimary]   = useState('');
  const [secondary, setSecondary] = useState('');
  const [message, setMessage]   = useState('');
  const [saving, setSaving]     = useState(false);

  async function submit() {
    if (!email.trim() || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSaving(true);
    try {
      const body = {
        email: email.trim(),
        division_id: divisionId || null,
        jersey_color_primary: primary.trim() || null,
        jersey_color_secondary: secondary.trim() || null,
        message: message.trim() || null,
      };
      const { data } = await api.post(`/leagues/${leagueId}/seasons/${seasonId}/team-invites`, body);
      if (data?.resent) {
        toast.success('Invite resent (an invite for this email was already pending)');
      } else {
        toast.success('Invite sent');
      }
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not send invite');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div onClick={onClose} style={modalBackdrop}>
      {/* v2.5.x — mobile: 90vh clips under the on-screen keyboard / browser
          chrome on iOS Safari + Capacitor. var(--mobile-h) tracks the actual
          visible viewport (falls back to 100dvh on desktop where it's unset). */}
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 540, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 className="section-title text-lg">Invite a team to this season</h2>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16 }}>
          We'll email the coach a magic link. They click it, pick which of their teams to attach,
          and the team joins this season.
        </p>

        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={fieldLabelStyle}>Coach's email</span>
          <input type="email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="coach@example.com" autoFocus style={inputStyle} />
        </label>

        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={fieldLabelStyle}>Suggested division <span style={fieldHintInlineStyle}>(optional — coach can change)</span></span>
          <select value={divisionId} onChange={(e) => setDivisionId(e.target.value)} style={inputStyle}>
            <option value="">No division</option>
            {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </label>

        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <label style={{ flex: 1 }}>
            <span style={fieldLabelStyle}>Jersey primary <span style={fieldHintInlineStyle}>(optional)</span></span>
            <input type="text" value={primary} maxLength={20}
              onChange={(e) => setPrimary(e.target.value)}
              placeholder="e.g. red" style={inputStyle} />
          </label>
          <label style={{ flex: 1 }}>
            <span style={fieldLabelStyle}>Jersey secondary <span style={fieldHintInlineStyle}>(optional)</span></span>
            <input type="text" value={secondary} maxLength={20}
              onChange={(e) => setSecondary(e.target.value)}
              placeholder="e.g. white" style={inputStyle} />
          </label>
        </div>

        <label style={{ display: 'block', marginBottom: 16 }}>
          <span style={fieldLabelStyle}>Personal message <span style={fieldHintInlineStyle}>(optional, shown in the email)</span></span>
          <textarea value={message} maxLength={500} rows={3}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Looking forward to having you in the league this season."
            style={{ ...inputStyle, resize: 'vertical' }} />
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-ice" onClick={submit}
            disabled={saving || !email.trim()}>
            {saving ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Start New Season wizard (multi-step)
// ───────────────────────────────────────────────────────────────

function StartSeasonWizard({ leagueId, existingSeasons, onClose, onCreated }) {
  // Step state: 0 = basics, 1 = carry-forward (skipped if no prior seasons), 2 = review
  const [step, setStep]     = useState(0);
  const [name, setName]     = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [importFromSeasonId, setImportFromSeasonId] = useState('');
  const [importTeams, setImportTeams]         = useState(true);
  const [importDivisions, setImportDivisions] = useState(true);
  const [saving, setSaving] = useState(false);

  const hasPriorSeasons = existingSeasons.length > 0;

  // If no prior seasons exist, skip the carry-forward step.
  // The Next button validates each step before allowing advance.
  function canNext() {
    if (step === 0) {
      if (!name.trim() || !startDate || !endDate) return false;
      if (endDate < startDate) return false;
      return true;
    }
    return true;
  }
  function next() {
    if (!canNext()) return;
    if (step === 0 && !hasPriorSeasons) setStep(2);
    else setStep(step + 1);
  }
  function back() {
    if (step === 2 && !hasPriorSeasons) setStep(0);
    else setStep(step - 1);
  }

  async function submit() {
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        start_date: startDate,
        end_date: endDate,
      };
      if (importFromSeasonId) {
        body.import_from_season_id = importFromSeasonId;
        body.import_teams = !!importTeams;
        body.import_divisions = !!importDivisions;
      }
      const { data } = await api.post(`/leagues/${leagueId}/seasons`, body);
      const t = data?.imported?.teams || 0;
      const d = data?.imported?.divisions || 0;
      if (t || d) {
        toast.success(`Season created — imported ${t} team${t === 1 ? '' : 's'} and ${d} division${d === 1 ? '' : 's'}`);
      } else {
        toast.success('Season created');
      }
      onCreated(data?.season);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not create season');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div onClick={onClose} style={modalBackdrop}>
      {/* v2.5.x — mobile: swap fixed 90vh for var(--mobile-h) so the wizard
          doesn't overflow past the visible viewport on Capacitor / iOS Safari. */}
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 560, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 className="section-title text-lg">Start a new season</h2>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        {/* Step indicator */}
        <StepDots step={step} hasPriorSeasons={hasPriorSeasons} />

        {step === 0 && (
          <WizardStepBasics
            name={name} setName={setName}
            startDate={startDate} setStartDate={setStartDate}
            endDate={endDate} setEndDate={setEndDate}
          />
        )}

        {step === 1 && hasPriorSeasons && (
          <WizardStepCarryForward
            existingSeasons={existingSeasons}
            importFromSeasonId={importFromSeasonId}
            setImportFromSeasonId={setImportFromSeasonId}
            importTeams={importTeams}
            setImportTeams={setImportTeams}
            importDivisions={importDivisions}
            setImportDivisions={setImportDivisions}
          />
        )}

        {step === 2 && (
          <WizardStepReview
            name={name} startDate={startDate} endDate={endDate}
            importFromSeasonId={importFromSeasonId}
            importTeams={importTeams} importDivisions={importDivisions}
            existingSeasons={existingSeasons}
          />
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, gap: 10, flexWrap: 'wrap' }}>
          {step > 0 ? (
            <button type="button" className="btn-ghost" onClick={back} disabled={saving}>
              <ArrowLeft size={13} style={{ marginRight: 6 }} />
              Back
            </button>
          ) : (
            <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          )}
          {step < 2 ? (
            <button type="button" className="btn-ice" onClick={next} disabled={!canNext()}>
              Next
              <ArrowRight size={13} style={{ marginLeft: 6 }} />
            </button>
          ) : (
            <button type="button" className="btn-ice" onClick={submit} disabled={saving}>
              {saving ? 'Creating…' : 'Create season'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepDots({ step, hasPriorSeasons }) {
  // Show 2 dots if no prior seasons (carry-forward step is skipped),
  // 3 dots otherwise. Step indices: 0 → basics, 1 → carry-forward,
  // 2 → review. When skipping, "current" is step 0 then 2 — the
  // middle dot is collapsed.
  const dots = hasPriorSeasons ? 3 : 2;
  // Translate step index to dot index for the no-prior case (0→0, 2→1)
  const displayCurrent = hasPriorSeasons ? step : (step === 2 ? 1 : 0);
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 18 }}>
      {Array.from({ length: dots }).map((_, i) => (
        <div key={i} style={{
          width: 8, height: 8, borderRadius: '50%',
          background: i === displayCurrent ? 'var(--ice)' : 'var(--surface-2)',
          border: i === displayCurrent ? 'none' : '1px solid var(--border)',
        }} />
      ))}
    </div>
  );
}

function WizardStepBasics({ name, setName, startDate, setStartDate, endDate, setEndDate }) {
  return (
    <>
      <label style={{ display: 'block', marginBottom: 12 }}>
        <span style={fieldLabelStyle}>Season name</span>
        <input type="text" value={name} maxLength={80}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Fall 2026, Spring Tournament" autoFocus
          style={inputStyle} />
      </label>
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <label style={{ flex: '1 1 180px' }}>
          <span style={fieldLabelStyle}>Start date</span>
          <input type="date" value={startDate}
            onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
        </label>
        <label style={{ flex: '1 1 180px' }}>
          <span style={fieldLabelStyle}>End date</span>
          <input type="date" value={endDate}
            onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
        </label>
      </div>
      {endDate && startDate && endDate < startDate && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--penalty)' }}>
          <AlertCircle size={12} />
          End date must be on or after the start date.
        </div>
      )}
    </>
  );
}

function WizardStepCarryForward({
  existingSeasons,
  importFromSeasonId, setImportFromSeasonId,
  importTeams, setImportTeams,
  importDivisions, setImportDivisions,
}) {
  return (
    <>
      <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16 }}>
        Copy structure forward from a prior season. <strong style={{ color: 'var(--text)' }}>Rosters never carry forward</strong> —
        each season starts fresh from the player pool.
      </p>

      <label style={{ display: 'block', marginBottom: 16 }}>
        <span style={fieldLabelStyle}>Import from</span>
        <select value={importFromSeasonId}
          onChange={(e) => setImportFromSeasonId(e.target.value)}
          style={inputStyle}>
          <option value="">Don't import — start blank</option>
          {existingSeasons.map(s => (
            <option key={s.id} value={s.id}>
              {s.name} ({s.status})
            </option>
          ))}
        </select>
      </label>

      {importFromSeasonId && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <CheckRow
            checked={importTeams}
            onChange={setImportTeams}
            label="Teams"
            sub="Carry the team list (not their rosters) into the new season."
          />
          <CheckRow
            checked={importDivisions}
            onChange={setImportDivisions}
            label="Divisions"
            sub="Copy division names and ordering. Teams are placed into matching divisions when both are imported."
          />
          <div style={{
            fontSize: 12, color: 'var(--text-3)',
            padding: 10, background: 'var(--surface-2)', borderRadius: 8,
            marginTop: 4,
          }}>
            Venues carry forward automatically (they're per-league, not per-season).
          </div>
        </div>
      )}
    </>
  );
}

function CheckRow({ checked, onChange, label, sub }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: 12, borderRadius: 10,
        background: checked ? 'var(--ice-a10)' : 'var(--surface-2)',
        border: `1px solid ${checked ? 'var(--ice-a40)' : 'var(--border)'}`,
        cursor: 'pointer', textAlign: 'left', color: 'var(--text)',
      }}
    >
      <div style={{
        width: 18, height: 18, borderRadius: 5,
        marginTop: 1, flexShrink: 0,
        background: checked ? 'var(--ice)' : 'var(--surface)',
        border: `1px solid ${checked ? 'var(--ice)' : 'var(--border)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && <CheckCircle2 size={12} color="var(--btn-accent-text)" strokeWidth={3} />}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>{sub}</div>
      </div>
    </button>
  );
}

function WizardStepReview({ name, startDate, endDate, importFromSeasonId, importTeams, importDivisions, existingSeasons }) {
  const source = existingSeasons.find(s => s.id === importFromSeasonId);
  return (
    <>
      <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16 }}>Confirm and create.</p>
      <div style={{
        background: 'var(--surface-2)', borderRadius: 10, padding: 14,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        <ReviewRow label="Name" value={name} />
        <ReviewRow label="Dates" value={`${startDate} → ${endDate}`} />
        {source ? (
          <>
            <ReviewRow label="Importing from" value={source.name} />
            <ReviewRow label="Carry teams" value={importTeams ? 'Yes' : 'No'} />
            <ReviewRow label="Carry divisions" value={importDivisions ? 'Yes' : 'No'} />
          </>
        ) : (
          <ReviewRow label="Carry-forward" value="None — blank season" />
        )}
      </div>
    </>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
      <span style={{ color: 'var(--text-2)' }}>{label}:</span>
      <span style={{ fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Shared bits
// ───────────────────────────────────────────────────────────────

// v2.5.124 — Phase 11 (date display). Previously had an inline
// helper here; extracted to utils/formatDate.js so all the league
// pages share the same format. The import at the top of the file
// brings in formatDateRange directly.

const inputStyle = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '8px 12px',
  color: 'var(--text)',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  outline: 'none',
};
const fieldLabelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 };
const fieldHintInlineStyle = { fontWeight: 400, opacity: 0.7 };
const tinyBtn = { padding: '4px 10px', fontSize: 11, minHeight: 'auto' };
const modalBackdrop = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 16, zIndex: 100,
};
